
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';

// Define valid authentication types
const VALID_AUTH_TYPES = ['signup', 'magiclink', 'invite', 'recovery', 'email_change', 'email'];

export const useAuthConfirmation = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const { user, refreshSession } = useAuth();
  
  // Check if we already have a valid session or if token is present
  useEffect(() => {
    if (user) {
      console.log("User already has an active session:", user.id);
      setSuccess(true);
      return;
    }
    
    // Just check if token exists in URL, but don't verify it automatically
    const tokenHash = searchParams.get('token_hash');
    
    if (tokenHash) {
      console.log("Token hash found in URL, waiting for user interaction");
      setHasToken(true);
    } else {
      setError('No valid authentication token found');
    }
  }, [searchParams, user]);

  // Verify the token hash only when explicitly called
  const handleVerifyToken = async (tokenHash: string, type: string) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Validate that the type is one of the allowed values
      if (!VALID_AUTH_TYPES.includes(type)) {
        throw new Error(`Invalid authentication type: ${type}`);
      }
      
      console.log(`Verifying ${type} with token hash:`, tokenHash);
      
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type as any, // Pass the validated type
      });
      
      if (error) {
        throw error;
      }
      
      if (data.session) {
        console.log("Successfully authenticated with session:", data.session.user.id);
        await refreshSession();
        setSuccess(true);
        toast.success('Authentication successful!');
      } else {
        throw new Error('No session returned from verification');
      }
    } catch (err: any) {
      console.error('Token verification error:', err);
      setError(err.message || 'Failed to verify authentication token');
      toast.error('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Manually trigger authentication
  const handleCompleteAuth = async () => {
    if (loading) return;
    
    // Check for token_hash in the URL
    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type') || 'magiclink';
    
    if (tokenHash) {
      await handleVerifyToken(tokenHash, type);
    } else {
      setError('No valid authentication token found');
      toast.error('Authentication failed: No valid token');
    }
  };

  return {
    loading,
    error,
    success,
    hasToken,
    handleCompleteAuth,
  };
};
